import React from 'react';
import { toast } from 'react-toastify';
import { makeStyles } from '@material-ui/core';
import { Loading } from '@hopdrive/storybook';

import { useWallet } from './useWallet';

import Divide from '../../reusable/Divide';
import StaticAlert from '../../utils/StaticAlert';
import WalletBadge from './WalletBadge';
import WalletCreate from './WalletCreate';
import PayoutsTable from './PayoutsTable';

//////////////////////////////////////// COMPONENT ////////////////////////////////////////

export default function WalletManagement({ driver }) {
  const cls = useStyles();

  const { getPayoutMethod } = useWallet();

  const [loading, setLoading] = React.useState(true);
  const [failed, setFailed] = React.useState(false);
  const [payoutMethod, setPayoutMethod] = React.useState(null);
  const [replacing, setReplacing] = React.useState(false);

  const fetchPayoutMethod = React.useCallback(async () => {
    setLoading(true);
    const res = await getPayoutMethod();
    if (res && res.success) {
      setPayoutMethod(res.data);
      setFailed(false);
    } else {
      setFailed(true);
      toast.error(res.message || `Failed to retrieve your bank account!`);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    fetchPayoutMethod();
  }, [driver, fetchPayoutMethod]);

  // A save or a removal answers with the new state, so there is nothing to refetch.
  const handleChanged = next => {
    setPayoutMethod(next);
    setReplacing(false);
  };

  if (loading) return <Loading position='relative' />;
  if (failed) return <StaticAlert type='error' message='Failed to load your bank account. Please try again later.' />;

  if (!payoutMethod || !payoutMethod.hasPayoutMethod || replacing) {
    return (
      <WalletCreate
        replacing={replacing}
        onSaved={handleChanged}
        onCancel={replacing ? () => setReplacing(false) : null}
      />
    );
  }

  return (
    <>
      <Divide>Manage Your Wallet</Divide>

      <div className={cls.badge}>
        <WalletBadge payoutMethod={payoutMethod} onRemoved={handleChanged} onReplace={() => setReplacing(true)} />
      </div>

      <Divide
        spacer
        tip={`Don't worry if this is empty. This only shows records from our new payouts system (which will be officially launched in January 2023).`}
      >
        View Your Recent Payouts
      </Divide>

      <PayoutsTable driver={driver} />
    </>
  );
}

//////////////////////////////////////// STYLES ////////////////////////////////////////

const useStyles = makeStyles(theme => ({
  badge: {
    position: 'relative',
    width: 'fit-content',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
}));
